import { type ReactNode, useState } from 'react'
import { BlockActionsMenu, type BlockAction } from './BlockActionsMenu'
import { cn } from '@/lib/utils'

interface Props {
  blockId: string
  /** Optional — kept for backwards compat. The visual "active" state is now
   *  driven by `:focus-within` (CSS-only) so it always matches whether the
   *  block actually has keyboard focus. The route-level `selectedIds` still
   *  flows through but no longer drives the border / background — it's
   *  reserved for explicit chat-reference selection later. */
  selected?: boolean
  slug?: string
  frozen?: boolean
  animate?: boolean
  /** Whether the inner block content is interactive (text-editable
   *  paragraph/heading/callout). Drives cursor + focus-within visual:
   *  - editable: pointer at rest, text caret when text is focused, input
   *    border on focus-within
   *  - non-editable (charts, lists): pointer at rest only, no input border */
  editable?: boolean
  actions: BlockAction[]
  onToggleSelect?: (id: string) => void
  children: ReactNode
}

/**
 * BlockShell wraps every block. The "active" visual is :focus-within driven
 * (input-style 1px border + faint ring), so it tracks browser focus exactly.
 * The hover toolbar stays visible while the dropdown is open OR while the
 * block is focused, so the menu doesn't disappear mid-interaction.
 */
export function BlockShell({
  blockId,
  selected: _selected = false,
  slug,
  frozen = false,
  animate = false,
  editable = false,
  actions,
  onToggleSelect,
  children,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <div
      data-testid={`block-shell-${blockId}`}
      data-state={frozen ? 'frozen' : 'idle'}
      data-animate={animate ? 'true' : 'false'}
      data-menu-open={menuOpen ? 'true' : 'false'}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-block-interactive]')) return
        onToggleSelect?.(blockId)
      }}
      className={cn(
        'group relative rounded-md border border-transparent px-3 py-2 mb-1.5 transition-colors',
        // Cursor: pointer at rest for clickable blocks; the contenteditable
        // child overrides with cursor-text via TextBlockBase.
        'cursor-pointer',
        // Hover bg — slightly more visible than muted/40.
        'hover:bg-muted',
        // Active visual: 1px input-style border + faint ring on focus-within
        // (only for editable blocks — non-editable blocks don't gain an
        // input border because they aren't inputs).
        editable &&
          'focus-within:border-input focus-within:ring-1 focus-within:ring-ring/30 focus-within:bg-background',
        // Freeze
        frozen && 'opacity-60 pointer-events-none',
        // Typewriter pulse on agent edits
        animate && 'bg-yellow-50',
      )}
    >
      {slug ? (
        <span
          className={cn(
            'absolute -top-2 left-3 text-[10px] text-muted-foreground bg-background px-1 transition-opacity',
            'opacity-0 group-hover:opacity-100',
            // also visible while the menu is open OR the block is focused
            menuOpen && 'opacity-100',
            'group-focus-within:opacity-100',
          )}
        >
          §{slug}
        </span>
      ) : null}
      <div
        data-block-interactive
        className={cn(
          'absolute -top-3 right-2 transition-opacity',
          // Visible on hover, when the dropdown is open, or when the block
          // is focused — the user can't lose the toolbar mid-interaction.
          'opacity-0 group-hover:opacity-100',
          menuOpen && 'opacity-100',
          'group-focus-within:opacity-100',
        )}
      >
        <BlockActionsMenu actions={actions} disabled={frozen} onOpenChange={setMenuOpen} />
      </div>
      {children}
    </div>
  )
}
